/**
 * legalContent.js — treść dokumentów prawnych Portalu Biura Przepustek KROSNO GLASS S.A.
 *
 * ŹRÓDŁA TREŚCI (nie zmyślać — każdy fakt ma pokrycie):
 *  1. INS-000-00-DP/01 „Instrukcja o ruchu osobowym", rew. 02 z 07.02.2023 — w szczególności
 *     pkt 5.4 i 5.5 (przepustki tymczasowe i jednorazowe), 5.10 (zakaz zdjęć i nagrań),
 *     5.11 (RODO), pkt 6 (retencja: 3 lata / 2 lata) oraz zał. 15 (zgody, „brak zgody = brak wstępu").
 *     Streszczenie: docs/INSTRUKCJA-RUCH-OSOBOWY-STRESZCZENIE.md.
 *  2. Faktyczne działanie aplikacji — PortalController.cs: zakres zbieranych danych (imię, nazwisko,
 *     nr dokumentu, data i miejsce urodzenia — PESEL usunięty z aplikacji decyzją klienta 2026-08-22 —
 *     e-mail, telefon + weryfikacja kodem SMS, firma, terminy i powody wejścia,
 *     strefa), tokeny (QrToken, TrackingToken, AccountSetupToken ważny 7 dni), logi skanowań QR
 *     (GuardScanLogs), adres IP zapisywany przy wysyłce kodu SMS, konto zewnętrzne + whitelista
 *     ważna 30 dni po zakończeniu okresu przepustki.
 *
 * STRUKTURA: dokument → sekcje → bloki. Typy bloków obsługiwane przez LegalDrawer:
 *   { type: "p",    text }            — akapit
 *   { type: "sub",  text }            — śródtytuł w sekcji
 *   { type: "ul",   items: [] }       — lista wypunktowana
 *   { type: "ol",   items: [] }       — lista numerowana
 *   { type: "dl",   items: [{term, text}] } — lista definicyjna (definicje, cele/podstawy prawne)
 *   { type: "note", text }            — wyróżniona ramka (rzeczy krytyczne)
 *
 * UWAGA: treść jest projektem roboczym przygotowanym przez zespół wdrożeniowy. Przed produkcją
 * musi ją zatwierdzić Inspektor Ochrony Danych — stąd `approvalNote` na końcu KAŻDEGO dokumentu.
 */

export const IOD_APPROVAL_NOTE =
    "Dokument wymaga zatwierdzenia przez Inspektora Ochrony Danych przed użyciem produkcyjnym.";

const ADMIN_NAME = "KROSNO GLASS S.A.";
const ADMIN_ADDRESS = "ul. Tysiąclecia 13, 38-400 Krosno";
const IOD_EMAIL = "iodo@krosno.com";
const CONTACT_EMAIL = "biuroprzepustek@krosno.com";
const SOURCE_DOC = "INS-000-00-DP/01 „Instrukcja o ruchu osobowym”, rewizja 02 z 07.02.2023";

/* ═══════════════════════════════════════════════════════════════════════════════
   1. KLAUZULA INFORMACYJNA RODO
   ═══════════════════════════════════════════════════════════════════════════════ */
const RODO = {
    kind: "rodo",
    title: "Informacja o przetwarzaniu danych osobowych",
    shortTitle: "Klauzula RODO",
    subtitle: "Klauzula informacyjna dla osób składających wniosek o przepustkę",
    version: "1.0 (projekt)",
    updatedAt: "2026-08-01",
    basis: `Podstawa wewnętrzna: ${SOURCE_DOC}, pkt 5.11 oraz zał. 15`,
    acceptLabel: "Akceptuję",
    acceptSummary:
        "Potwierdzam, że zapoznałam się / zapoznałem się z informacją o przetwarzaniu danych osobowych " +
        "i wyrażam zgodę na przetwarzanie moich danych w zakresie niezbędnym do rozpatrzenia wniosku " +
        "o przepustkę oraz wejścia na teren zakładu.",
    lead:
        "Zgodnie z art. 13 i 14 rozporządzenia Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r. " +
        "w sprawie ochrony osób fizycznych w związku z przetwarzaniem danych osobowych i w sprawie swobodnego przepływu " +
        "takich danych oraz uchylenia dyrektywy 95/46/WE (RODO) przekazujemy informacje o tym, kto przetwarza Twoje dane " +
        "osobowe podane w Portalu Biura Przepustek, w jakim celu, jak długo i jakie masz z tego tytułu prawa. " +
        "Prosimy o zapoznanie się z całością — przycisk akceptacji odblokuje się po przewinięciu dokumentu do końca.",
    sections: [
        {
            id: "administrator",
            heading: "1. Administrator danych osobowych",
            blocks: [
                {
                    type: "p",
                    text: `Administratorem Twoich danych osobowych jest ${ADMIN_NAME} z siedzibą w Krośnie, ${ADMIN_ADDRESS} (dalej: „Administrator”, „Spółka”, „Krosno Glass”).`,
                },
                {
                    type: "p",
                    text:
                        "Kontakt z Administratorem w sprawach dotyczących ruchu osobowego i przepustek: " +
                        `${CONTACT_EMAIL} lub pisemnie na adres siedziby Spółki z dopiskiem „Biuro Przepustek”.`,
                },
            ],
        },
        {
            id: "iod",
            heading: "2. Inspektor Ochrony Danych",
            blocks: [
                {
                    type: "p",
                    text:
                        "Administrator wyznaczył Inspektora Ochrony Danych (IOD), z którym możesz się skontaktować " +
                        "we wszystkich sprawach dotyczących przetwarzania Twoich danych osobowych oraz korzystania " +
                        "z przysługujących Ci praw:",
                },
                {
                    type: "ul",
                    items: [
                        `pocztą elektroniczną: ${IOD_EMAIL},`,
                        `pisemnie: ${ADMIN_NAME}, ${ADMIN_ADDRESS}, z dopiskiem „Inspektor Ochrony Danych”.`,
                    ],
                },
                {
                    type: "p",
                    text:
                        "Kontakt z IOD nie zastępuje złożenia wniosku o wydanie przepustki ani nie wstrzymuje jego rozpatrzenia — " +
                        "służy wyłącznie sprawom ochrony danych osobowych.",
                },
            ],
        },
        {
            id: "zrodlo",
            heading: "3. Skąd mamy Twoje dane",
            blocks: [
                {
                    type: "p",
                    text: "Twoje dane osobowe pozyskujemy:",
                },
                {
                    type: "ul",
                    items: [
                        "bezpośrednio od Ciebie — z formularza wniosku wypełnionego w Portalu Biura Przepustek, w tym z dobrowolnie dołączonego zdjęcia dokumentu tożsamości;",
                        "od podmiotu, który Cię zgłasza — jeżeli wniosek składa firma zewnętrzna, kontrahent lub pracownik Spółki zapraszający Cię na teren zakładu (art. 14 RODO); w takim przypadku otrzymujemy dane wskazane w imiennym wykazie osób wraz z serią i numerem dokumentu tożsamości ze zdjęciem;",
                        "z systemów Spółki — dane powstające w toku obsługi wniosku: numer wniosku i przepustki, status, treść i data decyzji, dane osoby akceptującej, zapisy skanowań kodu QR na portierni, znaczniki czasu i techniczne dane sesji.",
                    ],
                },
            ],
        },
        {
            id: "zakres",
            heading: "4. Jakie dane przetwarzamy",
            blocks: [
                {
                    type: "sub",
                    text: "4.1. Dane identyfikacyjne i kontaktowe",
                },
                {
                    type: "ul",
                    items: [
                        "imię i nazwisko,",
                        "data urodzenia oraz miejscowość i kraj urodzenia,",
                        "seria i numer dokumentu tożsamości ze zdjęciem (dowód osobisty, paszport lub inny dokument),",
                        "adres e-mail,",
                        "numer telefonu komórkowego wraz z informacją o dacie i godzinie jego potwierdzenia kodem SMS,",
                        "nazwa firmy lub podmiotu, który reprezentujesz (jeżeli została podana).",
                    ],
                },
                {
                    type: "sub",
                    text: "4.2. Dane dotyczące wizyty",
                },
                {
                    type: "ul",
                    items: [
                        "rodzaj wnioskowanej przepustki (jednorazowa albo tymczasowa),",
                        "terminy wizyt: daty od–do oraz godziny od–do dla każdego zgłoszonego okresu,",
                        "powód (cel) wejścia wskazany dla każdego okresu,",
                        "obszar (strefa) zakładu, którego dotyczy wniosek, oraz symbol portierni,",
                        "numer wniosku i numer przepustki, status wniosku, data i treść decyzji oraz imię i nazwisko osoby, która decyzję podjęła.",
                    ],
                },
                {
                    type: "sub",
                    text: "4.3. Zdjęcie dokumentu tożsamości (dobrowolne)",
                },
                {
                    type: "p",
                    text:
                        "Jeżeli dołączysz zdjęcie dokumentu tożsamości, przetwarzamy zawarty na nim obraz dokumentu wraz " +
                        "z widocznym wizerunkiem. Załączenie zdjęcia jest dobrowolne i odbywa się na podstawie Twojej zgody — " +
                        "służy przyspieszeniu weryfikacji na portierni. Rekomendujemy zasłonięcie danych, które nie są potrzebne " +
                        "do identyfikacji (np. wzoru podpisu). Zdjęcie możesz w każdej chwili usunąć przed wysłaniem wniosku, " +
                        "a po jego wysłaniu — wycofać zgodę i żądać usunięcia załącznika.",
                },
                {
                    type: "sub",
                    text: "4.4. Dane techniczne i zapisy z systemu",
                },
                {
                    type: "ul",
                    items: [
                        "adres IP, z którego wysłano żądanie kodu weryfikacyjnego SMS, oraz liczba prób weryfikacji (ochrona przed nadużyciami i spamem),",
                        "losowe tokeny techniczne: token śledzenia wniosku, token kodu QR oraz jednorazowy token zakładania konta (ważny 7 dni) — same w sobie nie zawierają danych osobowych,",
                        "zapisy skanowań kodu QR na portierni: data i godzina skanowania, wynik weryfikacji oraz oznaczenie osoby, która dokonała skanowania,",
                        "jeżeli założysz konto w Portalu — adres e-mail jako login, hasło w postaci nieodwracalnego skrótu (hash) oraz data utworzenia konta.",
                    ],
                },
                {
                    type: "sub",
                    text: "4.5. Dlaczego prosimy o numer dokumentu tożsamości i dane urodzenia",
                },
                {
                    type: "p",
                    text:
                        "Seria i numer dokumentu tożsamości oraz data i miejsce urodzenia nie są zbierane „na zapas”. Są konieczne, ponieważ:",
                },
                {
                    type: "ol",
                    items: [
                        "przepustka jest ważna wyłącznie razem z dokumentem tożsamości ze zdjęciem — Służba Ochrony na portierni porównuje dane z przepustki z okazanym dokumentem (pkt 5.4 i 5.5 Instrukcji o ruchu osobowym);",
                        "pozwalają jednoznacznie zidentyfikować osobę i wykluczyć pomyłkę przy zbieżności imion i nazwisk, co przy ruchu setek osób dziennie jest realnym ryzykiem;",
                        "przepustki i karty identyfikacyjne są drukami ścisłego zarachowania i podlegają imiennej ewidencji prowadzonej przez komórkę kadrową oraz Służbę Ochrony;",
                        "umożliwiają ustalenie, kto przebywa na terenie zakładu — co ma znaczenie krytyczne przy ewakuacji, wypadku przy pracy albo zdarzeniu wymagającym powiadomienia służb.",
                    ],
                },
                {
                    type: "note",
                    text:
                        "Nie zbieramy danych szczególnych kategorii (art. 9 RODO), takich jak dane o zdrowiu, poglądach czy przynależności związkowej. " +
                        "Jeżeli w polu „powód wejścia” wpiszesz takie informacje, zrobisz to z własnej inicjatywy — prosimy tego nie robić.",
                },
            ],
        },
        {
            id: "cele",
            heading: "5. Cele i podstawy prawne przetwarzania",
            blocks: [
                {
                    type: "p",
                    text:
                        "Każdy cel przetwarzania ma odrębną podstawę prawną. Poniżej wskazujemy je rozdzielnie, " +
                        "zgodnie z zasadą przejrzystości (art. 5 ust. 1 lit. a RODO):",
                },
                {
                    type: "dl",
                    items: [
                        {
                            term: "a) Obsługa wniosku o wydanie przepustki i kontakt w jego sprawie",
                            text:
                                "Rozpatrzenie wniosku, wydanie albo odmowa wydania przepustki, poinformowanie Cię o decyzji, " +
                                "udostępnienie linku do śledzenia statusu oraz kodu QR, kontakt telefoniczny lub e-mailowy w sprawie wizyty. " +
                                "Podstawa: art. 6 ust. 1 lit. b RODO (podjęcie działań przed zawarciem umowy oraz wykonanie umowy — gdy Twoja " +
                                "wizyta wiąże się z umową zawartą z Tobą lub z podmiotem, który reprezentujesz) oraz art. 6 ust. 1 lit. f RODO " +
                                "(prawnie uzasadniony interes Administratora polegający na obsłudze zgłoszenia osoby, która nie jest stroną umowy — " +
                                "gościa, interesanta, klienta).",
                        },
                        {
                            term: "b) Weryfikacja podanego numeru telefonu kodem SMS",
                            text:
                                "Potwierdzenie, że numer telefonu należy do osoby składającej wniosek, oraz ochrona Portalu przed składaniem " +
                                "wniosków na cudze dane i przed nadużyciami. Podstawa: art. 6 ust. 1 lit. f RODO (prawnie uzasadniony interes " +
                                "polegający na zapewnieniu wiarygodności zgłoszeń i bezpieczeństwa usługi).",
                        },
                        {
                            term: "c) Zapewnienie bezpieczeństwa osób i mienia oraz kontrola ruchu osobowego na terenie zakładu",
                            text:
                                "Ewidencja osób wchodzących i wychodzących, weryfikacja przepustki kodem QR na portierni i zapis wyniku tej " +
                                "weryfikacji, prowadzenie rejestrów wymaganych Instrukcją o ruchu osobowym, ustalenie osób przebywających " +
                                "na terenie zakładu na potrzeby ewakuacji i postępowań powypadkowych, ochrona tajemnicy przedsiębiorstwa. " +
                                "Podstawa: art. 6 ust. 1 lit. f RODO (prawnie uzasadniony interes Administratora polegający na zapewnieniu " +
                                "bezpieczeństwa osób przebywających na terenie zakładu oraz ochronie mienia i informacji Spółki).",
                        },
                        {
                            term: "d) Ustalenie, dochodzenie lub obrona roszczeń",
                            text:
                                "Wykazanie, kto i kiedy przebywał na terenie zakładu, w razie sporu, szkody, zdarzenia wypadkowego, " +
                                "kontroli organów albo postępowania sądowego. Podstawa: art. 6 ust. 1 lit. f RODO (prawnie uzasadniony interes " +
                                "polegający na możliwości ustalenia, dochodzenia i obrony roszczeń).",
                        },
                        {
                            term: "e) Przetwarzanie danych podanych dobrowolnie ponad zakres konieczny",
                            text:
                                "W szczególności zdjęcie dokumentu tożsamości wraz z wizerunkiem oraz dane kontaktowe podane ponad wymagane " +
                                "minimum. Podstawa: art. 6 ust. 1 lit. a RODO (Twoja zgoda), którą możesz w każdej chwili wycofać — " +
                                "wycofanie nie wpływa na zgodność z prawem przetwarzania dokonanego przed jej wycofaniem.",
                        },
                        {
                            term: "f) Prowadzenie opcjonalnego konta w Portalu",
                            text:
                                "Umożliwienie Ci podglądu własnych wniosków i przepustek w jednym miejscu oraz logowania hasłem. " +
                                "Podstawa: art. 6 ust. 1 lit. a RODO (zgoda wyrażona przez założenie konta) oraz art. 6 ust. 1 lit. b RODO " +
                                "(świadczenie usługi konta na Twoje żądanie). Konto jest opcjonalne — bez niego wniosek działa tak samo, " +
                                "a status śledzisz linkiem z wiadomości e-mail.",
                        },
                    ],
                },
            ],
        },
        {
            id: "odbiorcy",
            heading: "6. Odbiorcy danych",
            blocks: [
                {
                    type: "p",
                    text:
                        "Dostęp do Twoich danych mają wyłącznie osoby i podmioty, którym jest to niezbędne do wykonania " +
                        "wskazanych wyżej celów (zasada wiedzy koniecznej, need-to-know):",
                },
                {
                    type: "ul",
                    items: [
                        "upoważnieni pracownicy Administratora — Dział Kadr i Płac (Biuro Przepustek) prowadzący ewidencję przepustek, osoba odwiedzana oraz kadra kierownicza akceptująca wnioski, w zakresie niezbędnym do podjęcia decyzji;",
                        "Służba Ochrony — zewnętrzna firma ochrony obsługująca portiernie zakładu, przetwarzająca dane na podstawie umowy powierzenia przetwarzania (art. 28 RODO), w zakresie potrzebnym do wpuszczenia na teren, weryfikacji przepustki kodem QR i prowadzenia rejestrów;",
                        "dostawcy usług IT — podmiot utrzymujący i serwisujący system Biura Przepustek oraz infrastrukturę serwerową, na podstawie umowy powierzenia przetwarzania;",
                        "dostawca bramki SMS — podmiot przekazujący kod weryfikacyjny na wskazany przez Ciebie numer telefonu, na podstawie umowy powierzenia przetwarzania;",
                        "dostawca usługi poczty elektronicznej — podmiot obsługujący wysyłkę wiadomości e-mail z potwierdzeniem wniosku i linkiem do śledzenia statusu, na podstawie umowy powierzenia przetwarzania;",
                        "podmioty uprawnione na podstawie przepisów prawa — w szczególności Policja, Państwowa Inspekcja Pracy, sądy, prokuratura, organy nadzoru — wyłącznie na uzasadnione żądanie i w zakresie wynikającym z przepisów.",
                    ],
                },
                {
                    type: "p",
                    text:
                        "Wszystkie podmioty przetwarzające działają wyłącznie na udokumentowane polecenie Administratora, " +
                        "są zobowiązane do zachowania poufności i stosowania środków bezpieczeństwa odpowiadających wymogom RODO. " +
                        "Twoje dane nie są sprzedawane ani udostępniane w celach marketingowych.",
                },
            ],
        },
        {
            id: "eog",
            heading: "7. Przekazywanie danych poza Europejski Obszar Gospodarczy",
            blocks: [
                {
                    type: "p",
                    text:
                        "Twoje dane nie są przekazywane do państw trzecich (poza Europejski Obszar Gospodarczy) ani do organizacji " +
                        "międzynarodowych. Gdyby w przyszłości takie przekazanie okazało się konieczne — na przykład wskutek zmiany " +
                        "dostawcy usług informatycznych — nastąpi wyłącznie przy zastosowaniu zabezpieczeń przewidzianych " +
                        "w rozdziale V RODO (decyzja Komisji Europejskiej stwierdzająca odpowiedni stopień ochrony albo standardowe " +
                        "klauzule umowne, art. 45–46 RODO), a niniejsza klauzula zostanie odpowiednio zaktualizowana.",
                },
            ],
        },
        {
            id: "retencja",
            heading: "8. Okres przechowywania danych",
            blocks: [
                {
                    type: "p",
                    text:
                        "Okresy przechowywania wynikają z pkt 6 Instrukcji o ruchu osobowym oraz z przepisów powszechnie " +
                        "obowiązujących:",
                },
                {
                    type: "dl",
                    items: [
                        {
                            term: "Rejestry i ewidencje ruchu osobowego — 3 lata",
                            text:
                                "Rejestry przepustek jednorazowych i tymczasowych, zezwolenia na wejście klienta, specyfikacje narzędzi " +
                                "oraz pozostała dokumentacja przepustkowa prowadzona przez Dział Kadr i Płac oraz Służbę Ochrony są " +
                                "przechowywane przez 3 lata, po czym podlegają zniszczeniu (niszczarka / trwałe usunięcie z systemu). " +
                                "Wraz z rejestrem usuwane są powiązane zapisy skanowań kodu QR.",
                        },
                        {
                            term: "Zwrócone karty i przepustki — 2 lata od końca roku kalendarzowego",
                            text:
                                "Zwrócone karty identyfikacyjne i przepustki wraz z dokumentacją ich wydania są przechowywane przez 2 lata, " +
                                "licząc od końca roku kalendarzowego, w którym zostały zwrócone, a następnie niszczone. " +
                                "Przepustki nieważne są niszczone po zakończeniu roku, w którym utraciły ważność.",
                        },
                        {
                            term: "Wnioski nierozpatrzone i odrzucone",
                            text:
                                "Przechowywane do czasu wyjaśnienia sprawy, nie dłużej niż przez okres przedawnienia ewentualnych roszczeń.",
                        },
                        {
                            term: "Dane przetwarzane na podstawie zgody (m.in. zdjęcie dokumentu)",
                            text:
                                "Przechowywane do czasu wycofania zgody, a najpóźniej do upływu okresu przechowywania rejestru, " +
                                "którego dotyczą. Po wycofaniu zgody załącznik jest usuwany, a w rejestrze pozostają wyłącznie dane " +
                                "przetwarzane na innej podstawie prawnej (np. konieczne do wykazania, kto przebywał na terenie zakładu).",
                        },
                        {
                            term: "Dane związane z roszczeniami",
                            text:
                                "Do upływu terminu przedawnienia roszczeń wynikającego z Kodeksu cywilnego, a w razie wszczęcia " +
                                "postępowania — do jego prawomocnego zakończenia.",
                        },
                        {
                            term: "Konto w Portalu i wpis uprawniający do logowania",
                            text:
                                "Konto jest prowadzone do czasu jego usunięcia na Twoje żądanie. Uprawnienie do logowania jest ograniczone " +
                                "czasowo — wygasa 30 dni po zakończeniu okresu, którego dotyczyła przepustka. Jednorazowy link do założenia " +
                                "konta wygasa po 7 dniach od jego wysłania.",
                        },
                        {
                            term: "Kody weryfikacyjne SMS",
                            text:
                                "Kod jest ważny 10 minut i przechowywany wyłącznie w postaci nieodwracalnego skrótu. Zapisy prób weryfikacji " +
                                "(w tym adres IP) przechowujemy przez okres niezbędny do wykrywania nadużyć, nie dłużej niż przez okres " +
                                "przechowywania rejestru wniosków.",
                        },
                    ],
                },
            ],
        },
        {
            id: "prawa",
            heading: "9. Twoje prawa",
            blocks: [
                {
                    type: "p",
                    text: "W związku z przetwarzaniem Twoich danych osobowych przysługują Ci następujące prawa:",
                },
                {
                    type: "dl",
                    items: [
                        {
                            term: "Prawo dostępu do danych (art. 15 RODO)",
                            text:
                                "Możesz uzyskać potwierdzenie, czy przetwarzamy Twoje dane, uzyskać do nich dostęp oraz otrzymać ich kopię, " +
                                "a także informacje o celach, odbiorcach i okresie przechowywania.",
                        },
                        {
                            term: "Prawo do sprostowania (art. 16 RODO)",
                            text:
                                "Możesz żądać poprawienia danych nieprawidłowych oraz uzupełnienia danych niekompletnych — na przykład " +
                                "gdy w formularzu wystąpiła literówka w nazwisku albo numerze dokumentu.",
                        },
                        {
                            term: "Prawo do usunięcia danych, „prawo do bycia zapomnianym” (art. 17 RODO)",
                            text:
                                "Możesz żądać usunięcia danych, gdy nie są już niezbędne do celów, w których je zebrano, gdy wycofasz zgodę " +
                                "stanowiącą podstawę przetwarzania albo gdy wniesiesz skuteczny sprzeciw. Prawo to nie ma zastosowania " +
                                "w zakresie, w jakim przetwarzanie jest niezbędne do wywiązania się z obowiązków prawnych Administratora " +
                                "albo do ustalenia, dochodzenia lub obrony roszczeń.",
                        },
                        {
                            term: "Prawo do ograniczenia przetwarzania (art. 18 RODO)",
                            text:
                                "Możesz żądać ograniczenia przetwarzania — na przykład na czas weryfikacji prawidłowości zakwestionowanych " +
                                "przez Ciebie danych.",
                        },
                        {
                            term: "Prawo do przenoszenia danych (art. 20 RODO)",
                            text:
                                "W zakresie danych przetwarzanych na podstawie zgody albo umowy, w sposób zautomatyzowany, możesz otrzymać " +
                                "je w ustrukturyzowanym, powszechnie używanym formacie nadającym się do odczytu maszynowego i przesłać " +
                                "je innemu administratorowi.",
                        },
                        {
                            term: "Prawo sprzeciwu (art. 21 RODO)",
                            text:
                                "Wobec przetwarzania opartego na prawnie uzasadnionym interesie Administratora (art. 6 ust. 1 lit. f RODO) " +
                                "możesz w dowolnym momencie wnieść sprzeciw z przyczyn związanych z Twoją szczególną sytuacją. " +
                                "Po jego otrzymaniu przestaniemy przetwarzać dane, chyba że wykażemy istnienie ważnych, prawnie uzasadnionych " +
                                "podstaw nadrzędnych wobec Twoich interesów, praw i wolności — w praktyce najczęściej będzie to konieczność " +
                                "zapewnienia bezpieczeństwa osób i mienia na terenie zakładu.",
                        },
                        {
                            term: "Prawo do cofnięcia zgody (art. 7 ust. 3 RODO)",
                            text:
                                "Zgodę możesz wycofać w każdej chwili, bez podawania przyczyny, wysyłając wiadomość na adres IOD. " +
                                "Wycofanie zgody nie wpływa na zgodność z prawem przetwarzania dokonanego przed jej wycofaniem. " +
                                "Skutkiem wycofania zgody obejmującej dane konieczne do wejścia na teren zakładu jest brak możliwości " +
                                "wydania przepustki i brak wstępu.",
                        },
                        {
                            term: "Prawo skargi do organu nadzorczego",
                            text:
                                "Jeżeli uznasz, że przetwarzanie Twoich danych narusza przepisy, możesz wnieść skargę do Prezesa Urzędu " +
                                "Ochrony Danych Osobowych, ul. Stawki 2, 00-193 Warszawa. Zachęcamy, aby wcześniej skontaktować się " +
                                `z naszym Inspektorem Ochrony Danych (${IOD_EMAIL}) — wiele spraw da się wyjaśnić od ręki.`,
                        },
                    ],
                },
                {
                    type: "p",
                    text:
                        "Realizacja praw jest bezpłatna. Odpowiadamy bez zbędnej zwłoki, nie później niż w terminie miesiąca " +
                        "od otrzymania żądania; w sprawach skomplikowanych termin ten może zostać przedłużony o kolejne dwa miesiące, " +
                        "o czym Cię poinformujemy. Przed realizacją żądania możemy poprosić o dodatkowe informacje pozwalające " +
                        "potwierdzić Twoją tożsamość.",
                },
            ],
        },
        {
            id: "dobrowolnosc",
            heading: "10. Dobrowolność podania danych",
            blocks: [
                {
                    type: "p",
                    text:
                        "Podanie danych osobowych jest dobrowolne — nie masz prawnego obowiązku ich podania. Jest jednak konieczne " +
                        "do rozpatrzenia wniosku o przepustkę i wejścia na teren zakładu. Zgodnie z pkt 5.11 oraz zał. 15 Instrukcji " +
                        "o ruchu osobowym brak podania danych albo brak wymaganych zgód uniemożliwia wydanie przepustki, " +
                        "a tym samym skutkuje brakiem wstępu na teren Krosno Glass S.A.",
                },
                {
                    type: "note",
                    text:
                        "Krótko: bez podania danych nie możemy Cię zidentyfikować na portierni, a więc nie możemy wpuścić Cię " +
                        "na teren zakładu. Nie jest to sankcja, lecz konsekwencja zasad bezpieczeństwa obowiązujących wszystkie " +
                        "osoby wchodzące na teren Spółki.",
                },
            ],
        },
        {
            id: "profilowanie",
            heading: "11. Brak profilowania i zautomatyzowanego podejmowania decyzji",
            blocks: [
                {
                    type: "p",
                    text:
                        "Twoje dane nie podlegają profilowaniu ani zautomatyzowanemu podejmowaniu decyzji w rozumieniu " +
                        "art. 22 RODO. Decyzję o wydaniu albo odmowie wydania przepustki podejmuje zawsze uprawniony pracownik " +
                        "Spółki. System wykonuje wyłącznie czynności pomocnicze: waliduje poprawność podanych danych " +
                        "(np. sumę kontrolną numeru dokumentu tożsamości), kieruje wniosek do właściwej osoby akceptującej, wysyła powiadomienia " +
                        "i weryfikuje ważność kodu QR na portierni. Żadna z tych czynności nie zastępuje decyzji człowieka.",
                },
            ],
        },
        {
            id: "bezpieczenstwo",
            heading: "12. Bezpieczeństwo danych",
            blocks: [
                {
                    type: "p",
                    text:
                        "Administrator stosuje środki techniczne i organizacyjne zapewniające bezpieczeństwo przetwarzania " +
                        "odpowiednie do ryzyka (art. 32 RODO), w szczególności:",
                },
                {
                    type: "ul",
                    items: [
                        "szyfrowaną transmisję danych między przeglądarką a serwerem (HTTPS/TLS),",
                        "dostęp do danych wyłącznie dla osób posiadających imienne upoważnienie, w zakresie wynikającym z zakresu obowiązków,",
                        "kod QR nie zawiera Twoich danych osobowych — jest wyłącznie losowym, 192-bitowym tokenem, który dopiero po zeskanowaniu przez uprawnioną osobę odsłania dane niezbędne do weryfikacji,",
                        "hasła do kont przechowywane wyłącznie w postaci nieodwracalnego skrótu kryptograficznego,",
                        "ograniczenia liczby prób weryfikacji SMS i limity zapytań chroniące przed automatycznymi nadużyciami,",
                        "rejestrowanie operacji na wnioskach (kto, kiedy i jaką decyzję podjął, kto skanował przepustkę),",
                        "regularne kopie zapasowe oraz procedury reagowania na naruszenia ochrony danych osobowych.",
                    ],
                },
            ],
        },
        {
            id: "zmiany",
            heading: "13. Zmiany klauzuli informacyjnej",
            blocks: [
                {
                    type: "p",
                    text:
                        "Administrator może aktualizować niniejszą klauzulę, w szczególności w razie zmiany przepisów prawa, " +
                        "zakresu przetwarzanych danych albo listy podmiotów przetwarzających. Aktualna wersja jest zawsze dostępna " +
                        "w Portalu Biura Przepustek. Jeżeli zmiana będzie istotna, poprosimy o ponowne zapoznanie się z klauzulą " +
                        "przy najbliższym składaniu wniosku.",
                },
            ],
        },
    ],
    approvalNote: IOD_APPROVAL_NOTE,
};

/* ═══════════════════════════════════════════════════════════════════════════════
   2. REGULAMIN KORZYSTANIA Z PORTALU
   ═══════════════════════════════════════════════════════════════════════════════ */
const REGULAMIN = {
    kind: "regulamin",
    title: "Regulamin korzystania z Portalu Biura Przepustek",
    shortTitle: "Regulamin",
    subtitle: "Zasady świadczenia usługi drogą elektroniczną oraz wejścia na teren zakładu",
    version: "1.0 (projekt)",
    updatedAt: "2026-08-01",
    basis: `Podstawa wewnętrzna: ${SOURCE_DOC}`,
    acceptLabel: "Akceptuję",
    acceptSummary:
        "Oświadczam, że zapoznałam się / zapoznałem się z Regulaminem korzystania z Portalu Biura Przepustek, " +
        "akceptuję jego treść i zobowiązuję się do przestrzegania zasad wejścia i pobytu na terenie zakładu.",
    lead:
        `Regulamin określa zasady korzystania z Portalu Biura Przepustek ${ADMIN_NAME} — usługi świadczonej drogą ` +
        "elektroniczną, umożliwiającej złożenie wniosku o przepustkę na teren zakładu, śledzenie jego statusu " +
        "oraz otrzymanie kodu QR do okazania na portierni. Regulamin określa także obowiązki osoby, która na podstawie " +
        "wydanej przepustki wchodzi na teren zakładu. Prosimy o zapoznanie się z całością — przycisk akceptacji " +
        "odblokuje się po przewinięciu dokumentu do końca.",
    sections: [
        {
            id: "ogolne",
            heading: "§ 1. Postanowienia ogólne i definicje",
            blocks: [
                {
                    type: "p",
                    text:
                        `Usługodawcą i administratorem Portalu jest ${ADMIN_NAME} z siedzibą w Krośnie, ${ADMIN_ADDRESS}. ` +
                        "Regulamin jest regulaminem świadczenia usług drogą elektroniczną w rozumieniu ustawy z dnia 18 lipca 2002 r. " +
                        "o świadczeniu usług drogą elektroniczną i jest udostępniany nieodpłatnie przed zawarciem umowy, " +
                        "w sposób umożliwiający jego pozyskanie, odtworzenie i utrwalenie.",
                },
                { type: "sub", text: "Użyte pojęcia oznaczają:" },
                {
                    type: "dl",
                    items: [
                        { term: "Spółka / Usługodawca", text: `${ADMIN_NAME}, ${ADMIN_ADDRESS}.` },
                        {
                            term: "Portal",
                            text: "Serwis internetowy Biura Przepustek, w którym składa się i śledzi wnioski o przepustki.",
                        },
                        {
                            term: "Użytkownik / Wnioskodawca",
                            text:
                                "Osoba fizyczna korzystająca z Portalu, w szczególności składająca wniosek o przepustkę " +
                                "dla siebie albo dla osób, które reprezentuje.",
                        },
                        {
                            term: "Wniosek",
                            text:
                                "Elektroniczne zgłoszenie zamiaru wejścia na teren zakładu wraz z danymi osobowymi, terminami " +
                                "i powodem wejścia, złożone za pośrednictwem Portalu.",
                        },
                        {
                            term: "Przepustka jednorazowa",
                            text:
                                "Dokument uprawniający interesanta lub gościa do jednorazowego wejścia na teren zakładu, " +
                                "wydawany wraz z identyfikatorem GOŚĆ (pkt 5.5 Instrukcji).",
                        },
                        {
                            term: "Przepustka tymczasowa",
                            text:
                                "Dokument uprawniający do wielokrotnego wejścia w oznaczonym okresie, wydawany m.in. pracownikom " +
                                "firm zewnętrznych i osobom świadczącym usługi na podstawie umów cywilnoprawnych (pkt 5.4 Instrukcji).",
                        },
                        {
                            term: "Kod QR",
                            text:
                                "Losowy token graficzny udostępniany po akceptacji wniosku, służący do weryfikacji przepustki " +
                                "przez Służbę Ochrony na portierni.",
                        },
                        {
                            term: "Link śledzenia",
                            text:
                                "Adres z losowym tokenem, pod którym Użytkownik sprawdza status swojego wniosku bez zakładania konta.",
                        },
                        {
                            term: "Konto",
                            text:
                                "Opcjonalny, chroniony hasłem dostęp do Portalu, umożliwiający podgląd własnych wniosków i przepustek.",
                        },
                        {
                            term: "Służba Ochrony",
                            text: "Zewnętrzny podmiot obsługujący portiernie i kontrolę ruchu osobowego na terenie zakładu.",
                        },
                        {
                            term: "Biuro Przepustek",
                            text:
                                "Komórka organizacyjna Spółki (Dział Kadr i Płac) prowadząca ewidencję kart i przepustek oraz " +
                                "Portiernia Zakładowa wypisująca przepustki jednorazowe.",
                        },
                        {
                            term: "Instrukcja",
                            text: `Wewnętrzna ${SOURCE_DOC}, obowiązująca wszystkie osoby przebywające na terenie zakładu.`,
                        },
                        {
                            term: "Strefa / obszar",
                            text:
                                "Wyznaczona część terenu zakładu, do której uprawnia wydana przepustka, wskazana we wniosku " +
                                "i w decyzji o jego akceptacji.",
                        },
                    ],
                },
            ],
        },
        {
            id: "zakres",
            heading: "§ 2. Zakres usługi",
            blocks: [
                { type: "p", text: "W ramach Portalu Spółka świadczy nieodpłatnie następujące usługi:" },
                {
                    type: "ol",
                    items: [
                        "złożenie wniosku o przepustkę jednorazową albo tymczasową bez konieczności zakładania konta;",
                        "weryfikację podanego numeru telefonu komórkowego kodem przesłanym wiadomością SMS;",
                        "wysłanie na podany adres e-mail potwierdzenia przyjęcia wniosku wraz z linkiem śledzenia;",
                        "podgląd statusu wniosku i historii jego obsługi, w tym zapisów skanowań kodu QR na portierni;",
                        "udostępnienie kodu QR po akceptacji wniosku;",
                        "opcjonalne założenie konta pozwalającego zebrać wszystkie własne wnioski w jednym miejscu.",
                    ],
                },
                {
                    type: "note",
                    text:
                        "Złożenie wniosku nie jest równoznaczne z uzyskaniem prawa wstępu. Wniosek podlega rozpatrzeniu " +
                        "przez uprawnionego pracownika Spółki, a Spółka zastrzega sobie prawo odmowy wydania przepustki.",
                },
            ],
        },
        {
            id: "wymagania",
            heading: "§ 3. Wymagania techniczne",
            blocks: [
                { type: "p", text: "Do korzystania z Portalu niezbędne są:" },
                {
                    type: "ul",
                    items: [
                        "urządzenie z dostępem do internetu i aktualną przeglądarką internetową obsługującą JavaScript oraz pamięć lokalną przeglądarki;",
                        "aktywny adres poczty elektronicznej, na który trafia potwierdzenie wniosku i link śledzenia;",
                        "aktywny numer telefonu komórkowego zdolny do odbioru wiadomości SMS z kodem weryfikacyjnym;",
                        "opcjonalnie — aparat fotograficzny albo kamera w urządzeniu, jeżeli Użytkownik chce dołączyć zdjęcie dokumentu tożsamości.",
                    ],
                },
                {
                    type: "p",
                    text:
                        "Korzystanie z internetu wiąże się z ryzykiem typowym dla sieci publicznej. Spółka zaleca korzystanie " +
                        "z aktualnego oprogramowania i niekorzystanie z Portalu w niezaufanych sieciach publicznych.",
                },
            ],
        },
        {
            id: "wniosek",
            heading: "§ 4. Warunki złożenia wniosku",
            blocks: [
                {
                    type: "ol",
                    items: [
                        "Wniosek składa osoba pełnoletnia i posiadająca pełną zdolność do czynności prawnych. Wniosek dotyczący osoby małoletniej składa jej rodzic albo opiekun prawny, a wejście osoby małoletniej na teren zakładu wymaga odrębnej zgody i odbywa się wyłącznie pod opieką osoby dorosłej.",
                        "Wniosek zawiera komplet danych wymaganych formularzem: imię i nazwisko, serię i numer dokumentu tożsamości ze zdjęciem, datę urodzenia oraz miejscowość i kraj urodzenia, adres e-mail, numer telefonu komórkowego oraz — jeżeli dotyczy — nazwę reprezentowanej firmy.",
                        "Dla każdego terminu wizyty należy wskazać datę (opcjonalnie godziny) oraz powód wejścia liczący co najmniej 3 znaki. Powód powinien być zrozumiały dla osoby rozpatrującej wniosek.",
                        "Terminy wizyt muszą mieścić się w przedziale od dnia złożenia wniosku do jednego roku naprzód. Wniosek może obejmować najwyżej 12 okresów. Wniosek o przepustkę jednorazową obejmuje dokładnie jeden termin, który jest jednocześnie datą początkową i końcową.",
                        "Wniosek należy złożyć z rozsądnym wyprzedzeniem — zalecane jest co najmniej jeden dzień roboczy przed planowaną wizytą. Spółka nie gwarantuje rozpatrzenia wniosku złożonego w dniu wizyty.",
                        "Wskazanie obszaru (strefy) zakładu służy skierowaniu wniosku do właściwej osoby akceptującej. Przepustka uprawnia do przebywania wyłącznie we wskazanej strefie.",
                        "Dołączenie zdjęcia dokumentu tożsamości jest dobrowolne i przyspiesza weryfikację na portierni. Zdjęcie musi być czytelne i przedstawiać dokument należący do Wnioskodawcy.",
                    ],
                },
            ],
        },
        {
            id: "prawdziwosc",
            heading: "§ 5. Prawdziwość danych i odpowiedzialność Użytkownika",
            blocks: [
                {
                    type: "ol",
                    items: [
                        "Składając wniosek, Użytkownik oświadcza, że podane dane są prawdziwe, aktualne i dotyczą jego osoby albo osób, w imieniu których jest upoważniony działać.",
                        "Podanie danych innej osoby bez jej wiedzy i upoważnienia, posłużenie się cudzym numerem telefonu albo cudzym dokumentem tożsamości stanowi naruszenie Regulaminu i może stanowić czyn zabroniony.",
                        "Skutkiem podania danych nieprawdziwych jest odmowa wydania przepustki, unieważnienie przepustki już wydanej oraz odmowa wstępu albo wyproszenie z terenu zakładu.",
                        "W przypadku uzasadnionego podejrzenia popełnienia przestępstwa Spółka zawiadamia właściwe organy i przekazuje im dane niezbędne do prowadzenia postępowania.",
                        "Użytkownik ponosi odpowiedzialność za szkody wyrządzone Spółce albo osobom trzecim wskutek podania danych nieprawdziwych, udostępnienia przepustki lub kodu QR osobie nieuprawnionej albo naruszenia zasad pobytu na terenie zakładu.",
                        "Użytkownik zobowiązuje się niezwłocznie poinformować Biuro Przepustek o rezygnacji z wizyty albo o zmianie okoliczności mających znaczenie dla wydanej przepustki.",
                    ],
                },
            ],
        },
        {
            id: "sms",
            heading: "§ 6. Weryfikacja numeru telefonu",
            blocks: [
                {
                    type: "ol",
                    items: [
                        "Złożenie wniosku wymaga potwierdzenia numeru telefonu komórkowego jednorazowym, sześciocyfrowym kodem przesłanym wiadomością SMS.",
                        "Kod jest ważny 10 minut od wysłania. Dopuszczalne jest najwyżej 5 błędnych prób wpisania kodu — po ich wyczerpaniu należy wygenerować nowy kod.",
                        "Dla jednego numeru telefonu można wygenerować najwyżej 5 kodów w ciągu 30 minut. Ograniczenie chroni Użytkowników przed nadużyciami i nie podlega zniesieniu na żądanie.",
                        "Numer telefonu musi należeć do Wnioskodawcy albo osoby przez niego upoważnionej i pozostawać aktywny w okresie obowiązywania przepustki — służy do kontaktu w sprawie wizyty.",
                        "Wiadomość SMS jest bezpłatna dla odbiorcy; ewentualne koszty transmisji danych związane z korzystaniem z Portalu ponosi Użytkownik zgodnie z taryfą swojego operatora.",
                        "Jeżeli usługa wysyłki SMS jest chwilowo niedostępna, Spółka może dopuścić przyjęcie wniosku bez potwierdzenia numeru. Taki wniosek jest wyraźnie oznaczony jako „telefon niepotwierdzony” i wymaga dodatkowej weryfikacji przed wydaniem przepustki, co może wydłużyć czas obsługi.",
                    ],
                },
            ],
        },
        {
            id: "decyzja",
            heading: "§ 7. Rozpatrzenie wniosku i decyzja",
            blocks: [
                {
                    type: "ol",
                    items: [
                        "Wniosek rozpatruje uprawniony pracownik Spółki — w zależności od strefy i rodzaju przepustki jest to dyrektor, kierownik albo osoba przez nich upoważniona, zgodnie z Instrukcją. Decyzje nie są podejmowane automatycznie.",
                        "O zmianie statusu wniosku Użytkownik jest informowany pod linkiem śledzenia, a w miarę możliwości również wiadomością e-mail.",
                        "Spółka może zwrócić się o uzupełnienie wniosku albo o dodatkowe wyjaśnienia; brak odpowiedzi może skutkować pozostawieniem wniosku bez rozpatrzenia.",
                        "Spółka zastrzega sobie prawo odmowy wydania przepustki, a także cofnięcia przepustki już wydanej, bez obowiązku szczegółowego uzasadnienia, jeżeli wymagają tego względy bezpieczeństwa osób, mienia albo ochrony tajemnicy przedsiębiorstwa.",
                        "Wydana przepustka jest dokumentem ścisłego zarachowania i podlega ewidencji.",
                    ],
                },
            ],
        },
        {
            id: "qr",
            heading: "§ 8. Kod QR i posługiwanie się przepustką",
            blocks: [
                {
                    type: "ol",
                    items: [
                        "Kod QR jest udostępniany wyłącznie po akceptacji wniosku. Przed akceptacją wniosek nie stanowi przepustki i nie uprawnia do wejścia.",
                        "Kod QR jest ważny wyłącznie w terminach wskazanych w przepustce i wyłącznie razem z dokumentem tożsamości ze zdjęciem, na podstawie którego złożono wniosek.",
                        "Zabronione jest udostępnianie kodu QR, linku śledzenia oraz przepustki osobom trzecim. Przepustka jest imienna i nie może być odstąpiona innej osobie.",
                        "Każde skanowanie kodu QR na portierni jest rejestrowane wraz z datą, godziną, wynikiem weryfikacji i oznaczeniem osoby skanującej. Zapisy te są widoczne dla Użytkownika pod linkiem śledzenia.",
                        "W razie utraty urządzenia z kodem QR albo podejrzenia, że kod trafił w niepowołane ręce, należy niezwłocznie powiadomić Biuro Przepustek — przepustka zostanie unieważniona.",
                    ],
                },
            ],
        },
        {
            id: "pobyt",
            heading: "§ 9. Zasady wejścia i pobytu na terenie zakładu",
            blocks: [
                {
                    type: "p",
                    text:
                        "Wejście na teren zakładu odbywa się na zasadach określonych w Instrukcji. Akceptując Regulamin, " +
                        "Użytkownik zobowiązuje się w szczególności do:",
                },
                {
                    type: "ol",
                    items: [
                        "okazania na portierni ważnego dokumentu tożsamości ze zdjęciem — przepustka bez takiego dokumentu jest nieważna;",
                        "odebrania identyfikatora GOŚĆ (przy przepustce jednorazowej) i noszenia go w widocznym miejscu przez cały czas pobytu;",
                        "poruszania się wyłącznie w wyznaczonej strefie i najkrótszą drogą do miejsca wizyty oraz przebywania pod nadzorem albo w towarzystwie osoby odwiedzanej, jeżeli tak wskazano w decyzji;",
                        "przestrzegania przepisów bezpieczeństwa i higieny pracy, przepisów przeciwpożarowych, oznakowania na terenie zakładu oraz stosowania wymaganych środków ochrony indywidualnej;",
                        "wykonywania poleceń Służby Ochrony oraz poddania się kontroli uprawnionych pracowników;",
                        "powstrzymania się od wykonywania zdjęć, nagrań audio i wideo na terenie zakładu — jest to dozwolone wyłącznie za uprzednią zgodą Zarządu Spółki (pkt 5.10 Instrukcji);",
                        "zgłoszenia wnoszonych i wynoszonych narzędzi oraz urządzeń — podlegają one specyfikacji i kontroli przy wejściu oraz przy wyjściu;",
                        "przestrzegania zakazu wnoszenia i spożywania alkoholu oraz środków odurzających, a także zakazu wnoszenia przedmiotów niebezpiecznych;",
                        "potwierdzenia wizyty u osoby odwiedzanej (podpis oraz godzina zakończenia wizyty) — jeżeli wymaga tego przyjęta procedura;",
                        "opuszczenia terenu zakładu najkrótszą drogą oraz zwrotu przepustki i identyfikatora GOŚĆ na portierni; Służba Ochrony odnotowuje godzinę wyjścia.",
                    ],
                },
                {
                    type: "note",
                    text:
                        "Naruszenie powyższych zasad — w szczególności wykonywanie zdjęć bez zgody Zarządu, odstąpienie przepustki " +
                        "innej osobie albo przebywanie poza wyznaczoną strefą — skutkuje unieważnieniem przepustki, " +
                        "wyproszeniem z terenu zakładu oraz może stanowić podstawę odmowy wydania kolejnych przepustek.",
                },
            ],
        },
        {
            id: "godziny",
            heading: "§ 10. Godziny wydawania przepustek jednorazowych",
            blocks: [
                {
                    type: "ol",
                    items: [
                        "Przepustki jednorazowe są wydawane wyłącznie w dni pracy administracji Spółki, w godzinach od 6:00 do 15:00 (pkt 5.5 Instrukcji).",
                        "Wizyta poza tymi godzinami albo w dniu wolnym od pracy administracji wymaga odrębnej zgody Zarządu albo Dyrektora Operacyjnego i jest obsługiwana w trybie indywidualnym.",
                        "Wjazd na teren zakładu pojazdem prywatnym wymaga odrębnej zgody Zarządu; złożenie wniosku o przepustkę nie obejmuje zgody na wjazd pojazdem.",
                        "Odbiór towaru odbywa się na podstawie odrębnych dokumentów przewidzianych Instrukcją i nie jest realizowany na podstawie zgłoszenia w Portalu.",
                    ],
                },
            ],
        },
        {
            id: "konto",
            heading: "§ 11. Konto w Portalu",
            blocks: [
                {
                    type: "ol",
                    items: [
                        "Założenie konta jest dobrowolne i bezpłatne. Brak konta nie ogranicza możliwości złożenia wniosku ani śledzenia jego statusu.",
                        "Link umożliwiający założenie konta jest wysyłany wyłącznie na adres e-mail podany we wniosku, jest jednorazowy i traci ważność po 7 dniach.",
                        "Pracownicy Spółki oraz osoby posługujące się firmowym adresem e-mail logują się wyłącznie kontem Office 365 — dla takich adresów konto hasłowe nie jest zakładane.",
                        "Użytkownik zobowiązuje się chronić hasło, nie udostępniać konta osobom trzecim i niezwłocznie zgłosić podejrzenie nieuprawnionego dostępu.",
                        "Dostęp do konta jest ograniczony czasowo i wygasa po upływie 30 dni od zakończenia okresu, którego dotyczyła przepustka. Konto może zostać usunięte na żądanie Użytkownika.",
                        "Umowa o świadczenie usługi konta zawierana jest na czas nieoznaczony i może zostać wypowiedziana przez Użytkownika w każdej chwili poprzez żądanie usunięcia konta.",
                    ],
                },
            ],
        },
        {
            id: "zakazy",
            heading: "§ 12. Zakazane sposoby korzystania z Portalu",
            blocks: [
                { type: "p", text: "Zabronione jest w szczególności:" },
                {
                    type: "ul",
                    items: [
                        "podejmowanie prób obejścia zabezpieczeń Portalu, w tym mechanizmu weryfikacji SMS i limitów zapytań;",
                        "korzystanie z Portalu za pomocą automatów, skryptów i narzędzi masowego składania wniosków;",
                        "wprowadzanie treści bezprawnych, obraźliwych albo naruszających prawa osób trzecich, w tym w polu powodu wejścia;",
                        "dostarczanie złośliwego oprogramowania oraz plików mogących zakłócić działanie systemu;",
                        "wykorzystywanie Portalu w celu pozyskania danych osobowych innych osób albo informacji stanowiących tajemnicę przedsiębiorstwa.",
                    ],
                },
                {
                    type: "p",
                    text:
                        "W razie naruszenia Spółka może zablokować dostęp do Portalu oraz podjąć kroki przewidziane prawem.",
                },
            ],
        },
        {
            id: "dostepnosc",
            heading: "§ 13. Dostępność usługi i przerwy techniczne",
            blocks: [
                {
                    type: "p",
                    text:
                        "Spółka dokłada starań, aby Portal działał w sposób ciągły, nie gwarantuje jednak nieprzerwanej dostępności. " +
                        "Możliwe są przerwy techniczne związane z konserwacją, aktualizacją albo awarią, a także przerwy wynikające " +
                        "z okoliczności niezależnych od Spółki (siła wyższa, awaria łączy, awaria po stronie dostawcy usług SMS " +
                        "albo poczty elektronicznej). Niedostępność Portalu nie zwalnia z obowiązku przestrzegania zasad wejścia " +
                        "na teren zakładu; w takim przypadku wniosek można złożyć bezpośrednio w Biurze Przepustek.",
                },
            ],
        },
        {
            id: "reklamacje",
            heading: "§ 14. Reklamacje i kontakt",
            blocks: [
                {
                    type: "ol",
                    items: [
                        `Reklamacje dotyczące działania Portalu i obsługi wniosku można składać na adres ${CONTACT_EMAIL} albo pisemnie na adres siedziby Spółki z dopiskiem „Biuro Przepustek”.`,
                        "Reklamacja powinna zawierać: imię i nazwisko, adres e-mail podany we wniosku, numer wniosku (jeżeli został nadany), opis problemu oraz oczekiwany sposób załatwienia sprawy.",
                        "Spółka rozpatruje reklamację niezwłocznie, nie później niż w terminie 14 dni od jej otrzymania, i informuje o wyniku na adres e-mail, z którego reklamacja wpłynęła.",
                        `Sprawy dotyczące ochrony danych osobowych, w tym realizacji praw wynikających z RODO, kierować należy do Inspektora Ochrony Danych: ${IOD_EMAIL}.`,
                    ],
                },
            ],
        },
        {
            id: "dane",
            heading: "§ 15. Dane osobowe",
            blocks: [
                {
                    type: "p",
                    text:
                        "Zasady przetwarzania danych osobowych Użytkowników Portalu — administrator, cele i podstawy prawne, " +
                        "odbiorcy, okresy przechowywania oraz przysługujące prawa — określa odrębny dokument: " +
                        "„Informacja o przetwarzaniu danych osobowych”, dostępny w Portalu przed złożeniem wniosku. " +
                        `Administratorem danych jest ${ADMIN_NAME}, ${ADMIN_ADDRESS}; kontakt do Inspektora Ochrony Danych: ${IOD_EMAIL}.`,
                },
            ],
        },
        {
            id: "koncowe",
            heading: "§ 16. Postanowienia końcowe",
            blocks: [
                {
                    type: "ol",
                    items: [
                        "W sprawach nieuregulowanych Regulaminem stosuje się przepisy prawa polskiego, w szczególności Kodeksu cywilnego oraz ustawy o świadczeniu usług drogą elektroniczną.",
                        `Regulamin stanowi uzupełnienie wewnętrznej ${SOURCE_DOC}; w razie rozbieżności dotyczących zasad ruchu osobowego na terenie zakładu pierwszeństwo mają postanowienia Instrukcji.`,
                        "Spółka może zmienić Regulamin z ważnych przyczyn, w szczególności zmiany przepisów prawa, zmiany zakresu usługi albo zmian organizacyjnych. Zmieniony Regulamin jest publikowany w Portalu; do wniosku stosuje się wersję zaakceptowaną w chwili jego złożenia.",
                        "Regulamin jest dostępny w Portalu w formie umożliwiającej jego pobranie, odtworzenie i utrwalenie.",
                        "Regulamin obowiązuje od dnia jego opublikowania w Portalu.",
                    ],
                },
            ],
        },
    ],
    approvalNote: IOD_APPROVAL_NOTE,
};

export const LEGAL_DOCUMENTS = {
    rodo: RODO,
    regulamin: REGULAMIN,
};

export const LEGAL_KINDS = Object.keys(LEGAL_DOCUMENTS);

/** Zwraca dokument dla `kind`; przy nieznanym rodzaju oddaje klauzulę RODO (bezpieczny domyślny). */
export function getLegalDocument(kind) {
    return LEGAL_DOCUMENTS[kind] || LEGAL_DOCUMENTS.rodo;
}

export default LEGAL_DOCUMENTS;
